<template>
    <div class="filters-section">
        <div class="filters-main-container">
            <div class="scrollable-filters-wrapper">
                <div class="scrollable-filters" ref="scrollableFilters">
                    <FilterChip
                        type="select"
                        :value="cloneState"
                        :options="stateOptions"
                        :operator-selectable="false"
                        @change="onStateChange($event)"
                    />

                    <FilterChip
                        v-for="filter in staticFilterTemplates"
                        :key="filter.field"
                        :ref="`staticFilterChip_${filter.field}`"
                        :data-filter-field="filter.field"
                        :dataFilterField="filter.field"
                        :type="filter.type"
                        :value="getFilterValue(filter)"
                        :valueText="getFilterValueText(filter)"
                        :options="filter.options"
                        :multiple="filter.multiple"
                        :enable-search="filter.search"
                        :icon="filter.icon || 'label'"
                        :filter-name="filter.label"
                        :operator-selectable="filter.operatorSelectable"
                        :operator="getFilterOperator(filter)"
                        :clearable="true"
                        @clear="clearFilter(filter)"
                        @change="onFilterChange(filter, $event)"
                        @operator-change="onOperatorChange(filter, $event)"
                    />

                    <FilterChip
                        v-for="filter in getSelectedDynamicFilters()"
                        :key="filter.field"
                        :ref="`filterChip_${filter.field}`"
                        :data-filter-field="filter.field"
                        :dataFilterField="filter.field"
                        :type="filter.type"
                        :value="getFilterValue(filter)"
                        :valueText="getFilterValueText(filter)"
                        :options="filter.options"
                        :multiple="filter.multiple"
                        :icon="filter.icon || 'label'"
                        :filter-name="filter.label"
                        :removable="true"
                        :operator-selectable="filter.operatorSelectable"
                        :operator="getFilterOperator(filter)"
                        :enable-search="filter.search"
                        :composite-select-label="filter.compositeSelectLabel"
                        :composite-input-label="filter.compositeInputLabel"
                        :composite-input-placeholder="filter.compositeInputPlaceholder"
                        @change="onFilterChange(filter, $event)"
                        @remove="removeDynamicFilter(filter)"
                        @operator-change="onOperatorChange(filter, $event)"
                    />
                </div>
            </div>
        </div>

        <div class="action-buttons-container">
            <FilterChip
                ref="filterAddChip"
                type="add"
                :multiple="true"
                :value="selectedDynamicFilters"
                :options="dynamicFilterOptions"
                :enable-search="true"
                :label="$t('chat.filtersSection.addFilter')"
                class="filter-add-chip"
                @change="addDynamicFilter"
            />

            <FilterChip
                v-if="!sortDisabled"
                type="select"
                :value="sortValue"
                :options="sortOptions"
                :operator-selectable="false"
                :enable-search="false"
                @change="onSortChange"
            />
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";
import FilterChip from "./FilterChip.vue";
import MinimalPopoverSelect from "./MinimalPopoverSelect.vue";
import _ from "lodash";

export default {
    name: "FiltersSection",
    components: {
        FilterChip,
        MinimalPopoverSelect,
    },
    props: {
        total: {
            type: Number,
            default: null,
        },
        state: {
            type: String,
            required: true,
        },
        filters: {
            type: Array,
            required: true,
        },
        sortDisabled: {
            type: Boolean,
            default: false,
        },
        sort: {
            type: String,
            default: "relevance",
        },
    },
    data() {
        return {
            cloneState: this.state,
            cloneFilters: [],
            selectedDynamicFilters: [],
            sortValue: this.sort,
        };
    },
    computed: {
        ...mapState({
            users: (state) => state.user.users.data || [],
            channels: (state) => state.channel.channels || [],
            tags: (state) => state.tag.tags.data || [],
            allContactProperties: (state) => state.contactProperty.data || [],
            departments: (state) => state.subject.subjects.data || [],
        }),
        zohoChannelExists() {
            return this.channels.some((channel) => channel.type === "zoho");
        },
        stateOptions() {
            // lastConversationStatus filtresi varsa sadece "all" seçeneği göster
            if (this.hasLastConversationStatusFilter) {
                return [
                    { label: this.$t('chat.filtersSection.all'), value: "all", icon: "label" },
                ];
            }

            return [
                { label: this.$t('chat.filtersSection.all'), value: "all", icon: "label" },
                { label: this.$t('chat.filtersSection.open'), value: "open", icon: "lock_open" },
                { label: this.$t('chat.filtersSection.closed'), value: "closed", icon: "lock" },
            ];
        },
        sortOptions() {
            return [
                { label: this.$t('chat.filtersSection.relevance'), value: "relevance" },
                { label: this.$t('chat.filtersSection.lastActivityNewest'), value: "lastActionTime_DESC" },
                { label: this.$t('chat.filtersSection.lastActivityOldest'), value: "lastActionTime_ASC" },
            ];
        },
        departmentOptions() {
            return this.departments.map((department) => ({
                label:
                    _.get(department, `languageContent.${this.$i18n.locale}.name`) ||
                    _.get(department, `languageContent.${Object.keys(department.languageContent)[0]}.name`),
                value: department._id,
                icon: "label",
            }));
        },
        channelOptions() {
            const communicationChannelTypes = [
                "webchat",
                "whatsapp-business-api",
                "whatsapp-business-api-supsis",
                "whatsapp-v2",
                "instagram",
                "instagram-v2",
                "messenger",
                "telegram-unipile",
                "linkedin-unipile",
                "whatsapp-unipile",
            ];

            const channelGroups = {
                webchat: "Webchat",
                "whatsapp-business-api": "WhatsApp Business API",
                "whatsapp-business-api-supsis": "WhatsApp Business API",
                "whatsapp-v2": "WhatsApp V2",
                instagram: "Instagram",
                "instagram-v2": "Instagram V2",
                messenger: "Messenger",
                "telegram-unipile": "Telegram",
                "linkedin-unipile": "LinkedIn",
                "whatsapp-unipile": "WhatsApp",
            };
            const channelIcons = {
                webchat: "SupsisIcon",
                "whatsapp-business-api": "WhatsappBusinessIcon",
                "whatsapp-business-api-supsis": "WhatsappBusinessIcon",
                "whatsapp-v2": "WhatsappV2",
                instagram: "InstagramIcon",
                "instagram-v2": "InstagramIcon",
                messenger: "FacebookIcon",
                "telegram-unipile": "TelegramIcon",
                "linkedin-unipile": "LinkedinIcon",
                "whatsapp-unipile": "WhatsappV3Icon",
            };

            return this.channels
                .filter((channel) => communicationChannelTypes.includes(channel.type))
                .map((channel) => ({
                    group: channelGroups[channel.type],
                    label: channel.name,
                    value: channel._id,
                    icon: channelIcons[channel.type],
                    iconType: "component",
                }));
        },
        platformOptions() {
            return [
                { label: "Supsis", value: "supsis", icon: "label" },
                { label: "Email", value: "email", icon: "label" },
                { label: "WhatsApp", value: "whatsapp", icon: "label" },
                { label: "Web", value: "web", icon: "label" },
                { label: "Instagram", value: "instagram", icon: "label" },
                { label: "Telegram", value: "telegram", icon: "label" },
                { label: "Google", value: "google", icon: "label" },
                { label: "Messenger", value: "messenger", icon: "label" },
                { label: "Calendar", value: "calendar", icon: "label" },
                { label: "Meta Leads", value: "meta-leads", icon: "label" },
                { label: "Trendyol", value: "trendyol", icon: "label" },
                { label: "LinkedIn", value: "linkedin", icon: "label" },
            ];
        },
        tagOptions() {
            return this.tags.map((tag) => ({
                label: tag.name,
                value: tag._id,
                icon: "label",
            }));
        },
        participantOptions() {
            return [
                { label: this.$t('chat.filtersSection.noParticipant'), value: "none", icon: "label" },
                ...this.users.map((user) => ({
                    label: user.name,
                    value: user._id,
                    icon: "label",
                })),
            ];
        },
        availableFiltersOptions() {
            return [
                { label: this.$t('chat.filtersSection.ownershipFilter'), value: "owner", icon: "label" },
                { label: this.$t('chat.filtersSection.metaReferral'), value: "metaReferral", icon: "label" },
                { label: this.$t('chat.filtersSection.userStatus'), value: "blocked", icon: "label" },
            ];
        },
        ownershipOptions() {
            return [
                { label: this.$t('chat.filtersSection.unowned'), value: "unowned", icon: "label" },
                ...this.users.map((user) => ({
                    label: user.name,
                    value: user._id,
                    icon: "label",
                })),
            ];
        },
        metaReferralOptions() {
            return [
                { label: this.$t('chat.filtersSection.fromAds'), value: true, icon: "label" },
                { label: this.$t('chat.filtersSection.notFromAds'), value: false, icon: "label" },
            ];
        },
        blockedOptions() {
            return [
                { label: this.$t('chat.filtersSection.blockedUsers'), value: "true", icon: "block" },
                { label: this.$t('chat.filtersSection.unblockedUsers'), value: "false", icon: "check_circle" },
            ];
        },
        unreadOptions() {
            return [
                { label: this.$t('chat.filtersSection.unread'), value: true, icon: "mark_email_unread" },
                { label: this.$t('chat.filtersSection.read'), value: false, icon: "mark_email_read" },
            ];
        },
        lastConversationIsMissedOptions() {
            return [
                { label: this.$t('chat.filtersSection.yes'), value: true, icon: "sentiment_dissatisfied" },
                { label: this.$t('chat.filtersSection.no'), value: false, icon: "check_circle_outline" },
            ];
        },
        answerStatusOptions() {
            return [
                { label: this.$t('chat.filtersSection.answered'), value: "answered", icon: "check_circle" },
                { label: this.$t('chat.filtersSection.unanswered'), value: "unanswered", icon: "help_outline" },
            ];
        },
        conversationStatusOptions() {
            return [
                { label: this.$t('chat.filtersSection.active'), value: "active", icon: "chat_bubble" },
                { label: this.$t('chat.filtersSection.lost'), value: "lost", icon: "sentiment_dissatisfied" },
                { label: this.$t('chat.filtersSection.new'), value: "new", icon: "fiber_new" },
                { label: this.$t('chat.filtersSection.chatbot'), value: "chatbot", icon: "smart_toy" },
                { label: this.$t('chat.filtersSection.closedStatus'), value: "destroyed", icon: "cancel" },
            ];
        },
        filterTemplates() {
            const contactPropertyFilters = this.allContactProperties.map((property) => {
                let filterType = "text";
                let multiple = false;
                let operatorSelectable = false;
                let search = true;
                let options = [];

                switch (property.type) {
                    case "number":
                        filterType = "number";
                        multiple = false;
                        operatorSelectable = true;
                        search = false;
                        break;
                    case "date":
                        filterType = "date";
                        multiple = false;
                        operatorSelectable = true;
                        search = false;
                        break;
                    case "agent":
                        filterType = "select";
                        multiple = true;
                        operatorSelectable = true;
                        search = true;
                        options = this.users.map(function (user) {
                            return {
                                label: user.name,
                                value: user._id,
                                icon: "label",
                            };
                        });
                        break;
                    case "select":
                        filterType = "select";
                        multiple = true;
                        operatorSelectable = true;
                        search = true;
                        options = property.options.map((opt) => ({
                            label: opt,
                            value: opt,
                        }));
                        break;
                    case "text":
                    default:
                        filterType = "text";
                        multiple = false;
                        operatorSelectable = true;
                        search = false;
                        break;
                }

                return {
                    dynamic: true,
                    group: "Contact Properties",
                    type: filterType,
                    field: `contactProperties.${property._id}`,
                    multiple: multiple,
                    operatorSelectable: operatorSelectable,
                    search: search,
                    options: options,
                    icon: "data_object",
                    label: property.label || property.name,
                };
            });

            return [
                {
                    type: "select",
                    field: "platform",
                    multiple: true,
                    search: true,
                    options: this.platformOptions,
                    label: this.$t('chat.filtersSection.platform'),
                    icon: "devices",
                },
                {
                    type: "select",
                    field: "channel",
                    multiple: true,
                    search: true,
                    options: this.channelOptions,
                    label: this.$t('chat.filtersSection.channel'),
                    icon: "forum",
                },
                {
                    type: "select",
                    field: "relatedDepartments",
                    multiple: true,
                    search: true,
                    options: this.departmentOptions,
                    label: this.$t('chat.filtersSection.department'),
                    icon: "business",
                },
                {
                    type: "select",
                    field: "tags",
                    multiple: true,
                    search: true,
                    options: this.tagOptions,
                    label: this.$t('chat.filtersSection.tag'),
                    icon: "local_offer",
                },
                {
                    type: "select",
                    field: "lastConversationUsers",
                    multiple: true,
                    search: true,
                    options: this.participantOptions,
                    label: this.$t('chat.filtersSection.participant'),
                    icon: "group",
                },
                {
                    type: "select",
                    field: "owner",
                    multiple: true,
                    search: true,
                    options: this.ownershipOptions,
                    label: this.$t('chat.filtersSection.ownership'),
                    icon: "person",
                },
                {
                    type: "date",
                    field: "lastActionTime",
                    label: this.$t('chat.filtersSection.lastActivityTime'),
                    multiple: false,
                    operatorSelectable: true,
                    search: false,
                    icon: "schedule",
                },
                {
                    dynamic: true,
                    type: "text",
                    field: "name",
                    label: this.$t('chat.filtersSection.fullName'),
                    multiple: false,
                    operatorSelectable: true,
                    search: false,
                    icon: "person",
                },
                {
                    dynamic: true,
                    type: "text",
                    field: "email",
                    label: this.$t('chat.filtersSection.email'),
                    multiple: false,
                    operatorSelectable: true,
                    search: false,
                    icon: "email",
                },
                {
                    dynamic: true,
                    type: "text",
                    field: "phone",
                    label: this.$t('chat.filtersSection.phone'),
                    multiple: false,
                    operatorSelectable: true,
                    search: false,
                    icon: "phone",
                },
                {
                    dynamic: true,
                    type: "select",
                    field: "vipCustomer",
                    multiple: false,
                    search: false,
                    label: this.$t('chat.filtersSection.vipCustomer'),
                    options: [
                        { label: this.$t('chat.filtersSection.hasVIP'), value: true, icon: "star" },
                        { label: this.$t('chat.filtersSection.noVIP'), value: false, icon: "star_border" },
                    ],
                    icon: "star",
                },
                {
                    dynamic: true,
                    type: "text",
                    field: "os",
                    label: this.$t('chat.filtersSection.operatingSystem'),
                    multiple: false,
                    operatorSelectable: true,
                    search: false,
                    icon: "computer",
                },
                {
                    dynamic: true,
                    type: "text",
                    field: "browser",
                    label: this.$t('chat.filtersSection.browser'),
                    multiple: false,
                    operatorSelectable: true,
                    search: false,
                    icon: "web",
                },
                {
                    dynamic: true,
                    type: "text",
                    field: "ipAddress",
                    label: this.$t('chat.filtersSection.ipAddress'),
                    multiple: false,
                    operatorSelectable: true,
                    search: false,
                    icon: "router",
                },
                {
                    dynamic: true,
                    type: "text",
                    field: "visitingPage",
                    label: this.$t('chat.filtersSection.visitingPage'),
                    multiple: false,
                    operatorSelectable: true,
                    search: false,
                    icon: "web",
                },
                {
                    dynamic: true,
                    type: "select",
                    field: "metaReferral",
                    options: this.metaReferralOptions,
                    label: this.$t('chat.filtersSection.adInteraction'),
                    multiple: false,
                    search: false,
                    icon: "ads_click",
                },
                {
                    dynamic: true,
                    type: "select",
                    field: "blocked",
                    options: this.blockedOptions,
                    label: this.$t('chat.filtersSection.blockedStatus'),
                    multiple: false,
                    icon: "block",
                },
                {
                    dynamic: true,
                    type: "date",
                    field: "connectionTime",
                    label: this.$t('chat.filtersSection.connectionTime'),
                    multiple: false,
                    operatorSelectable: true,
                    search: false,
                    icon: "schedule",
                },
                {
                    dynamic: true,
                    type: "date",
                    field: "createdAt",
                    label: this.$t('chat.filtersSection.createdAt'),
                    multiple: false,
                    operatorSelectable: true,
                    search: false,
                    icon: "schedule",
                },
                {
                    dynamic: true,
                    type: "date",
                    field: "updatedAt",
                    label: this.$t('chat.filtersSection.updatedAt'),
                    multiple: false,
                    operatorSelectable: true,
                    search: false,
                    icon: "update",
                },

                {
                    dynamic: true,
                    type: "select",
                    field: "unread",
                    options: this.unreadOptions,
                    label: this.$t('chat.filtersSection.readStatus'),
                    multiple: false,
                    search: false,
                    icon: "mark_email_unread",
                },
                {
                    dynamic: true,
                    type: "select",
                    field: "lastConversationIsMissed",
                    options: this.lastConversationIsMissedOptions,
                    label: this.$t('chat.filtersSection.missedChat'),
                    multiple: false,
                    search: false,
                    icon: "error_outline",
                },
                {
                    dynamic: true,
                    type: "select",
                    field: "answerStatus",
                    options: this.answerStatusOptions,
                    label: this.$t('chat.filtersSection.answerStatus'),
                    multiple: false,
                    search: false,
                    icon: "question_answer",
                },
                {
                    dynamic: true,
                    type: "text",
                    field: "whatsappId",
                    label: "Whatsapp ID",
                    multiple: false,
                    operatorSelectable: true,
                    search: false,
                    icon: "label",
                },
                {
                    dynamic: true,
                    type: "select",
                    field: "lastConversationStatus",
                    options: this.conversationStatusOptions,
                    label: this.$t('chat.filtersSection.lastConversationStatus'),
                    multiple: false,
                    search: false,
                    icon: "chat_bubble",
                },
                {
                    dynamic: true,
                    type: "composite",
                    field: "lastTriggerState",
                    options: [
                        { label: "API", value: "api" },
                        { label: "Broadcast", value: "broadcast" },
                        { label: "Automation", value: "automation" },
                    ],
                    label: this.$t('chat.filtersSection.lastTriggerState'),
                    compositeSelectLabel: this.$t('chat.filtersSection.type'),
                    compositeInputLabel: this.$t('chat.filtersSection.name'),
                    compositeInputPlaceholder: this.$t('chat.filtersSection.enterTriggerName'),
                    multiple: false,
                    search: false,
                    icon: "settings_input_component",
                },
                this.zohoChannelExists && {
                    group: "Zoho",
                    dynamic: true,
                    type: "composite",
                    field: "zohoInfo",
                    options: [
                        { label: "Contact", value: "contact" },
                        { label: "Lead", value: "lead" },
                    ],
                    label: this.$t('chat.filtersSection.zohoInfo'),
                    compositeSelectLabel: this.$t('chat.filtersSection.type'),
                    compositeInputLabel: this.$t('chat.filtersSection.id'),
                    compositeInputPlaceholder: this.$t('chat.filtersSection.enterContactOrLeadId'),
                    multiple: false,
                    search: false,
                    icon: "business",
                },
                {
                    group: "WhatsApp Broadcast",
                    dynamic: true,
                    type: "broadcast-composite",
                    field: "lastBroadcastInfo",
                    label: this.$t('chat.filtersSection.broadcastMessage'),
                    multiple: false,
                    search: false,
                    icon: "campaign",
                },
                {
                    dynamic: true,
                    type: "meta-referral-composite",
                    field: "metaReferralFields",
                    label: this.$t('chat.filtersSection.metaReferralFields'),
                    multiple: false,
                    search: false,
                    icon: "ads_click",
                },
                ...contactPropertyFilters,
            ].filter(Boolean);
        },
        dynamicFilterTemplates() {
            return this.filterTemplates.filter((filter) => filter.dynamic);
        },
        dynamicFilterOptions() {
            return this.dynamicFilterTemplates.map((filter) => ({
                icon: filter.icon,
                label: filter.label,
                value: filter.field,
                group: filter.group || null,
            }));
        },
        staticFilterTemplates() {
            return this.filterTemplates.filter((filter) => !filter.dynamic);
        },
        filterValues() {
            return this.cloneFilters.reduce((acc, filter) => {
                acc[filter.field] = filter.value;
                return acc;
            }, {});
        },
        hasLastConversationStatusFilter() {
            return this.cloneFilters.some(f => f.field === "lastConversationStatus" && f.value);
        },
    },
    methods: {
        getSelectedDynamicFilters() {
            const dynamicFilters = this.dynamicFilterTemplates.reduce((acc, filter) => {
                acc[filter.field] = filter;
                return acc;
            }, {});
            return this.selectedDynamicFilters.map((filterKey) => dynamicFilters[filterKey]);
        },
        getFilterValue(filter) {
            if (filter.field === "owner") {
                return (this.filterValues[filter.field] || []).map((v) => v.value);
            } else if (filter.field === "lastConversationUsers") {
                return (this.filterValues[filter.field] || []).map((v) => v.value);
            }
            return this.filterValues[filter.field];
        },
        getFilterOperator(filter) {
            const existingFilter = this.cloneFilters.find((f) => f.field === filter.field);
            if (existingFilter && existingFilter.operator) {
                return existingFilter.operator;
            }
            if (filter.field && filter.field.includes("contactProperties.")) {
                if (filter.type === "select") {
                    return "contains";
                }
                if (filter.type === "text") {
                    return "contains";
                }
                if (filter.type === "number") {
                    return "equals";
                }
            }
            return null;
        },
        getFilterValueText(filter) {
            let valueText = null;
            if (filter.type === "date") {
                const filterValue = this.getFilterValue(filter);
                const relativePresets = ["today", "yesterday", "thisWeek", "lastWeek", "last7Days", "last30Days", "thisMonth", "lastMonth"];

                if (typeof filterValue === "string" && relativePresets.includes(filterValue)) {
                    const presetLabels = {
                        today: this.$t('chat.filtersSection.today'),
                        yesterday: this.$t('chat.filtersSection.yesterday'),
                        thisWeek: this.$t('chat.filtersSection.thisWeek'),
                        lastWeek: this.$t('chat.filtersSection.lastWeek'),
                        last7Days: this.$t('chat.filtersSection.last7Days'),
                        last30Days: this.$t('chat.filtersSection.last30Days'),
                        thisMonth: this.$t('chat.filtersSection.thisMonth'),
                        lastMonth: this.$t('chat.filtersSection.lastMonth')
                    };
                    valueText = presetLabels[filterValue] || filterValue;
                } else if (Array.isArray(filterValue) && filterValue.length === 2) {
                    // Between için iki tarih
                    const startDate = filterValue[0];
                    const endDate = filterValue[1];
                    const formatDate = (date) => {
                        return this.$moment(date).format("MMM D, YYYY");
                    };
                    valueText = startDate && endDate ? `${formatDate(startDate)} - ${formatDate(endDate)}` : null;
                } else if (filterValue) {
                    // Tek tarih için (equals, greater_than vb.)
                    const formatDate = (date) => {
                        return this.$moment(date).format("MMM D, YYYY");
                    };
                    valueText = formatDate(filterValue);
                }
            } else if (filter.type === "select") {
                const optionMap = filter.options.reduce((acc, opt) => {
                    acc[opt.value] = opt.label;
                    if (opt.value != null && opt.value !== "") {
                        acc[String(opt.value)] = opt.label;
                    }
                    return acc;
                }, {});
                const rawVal = this.getFilterValue(filter);
                let values = [];
                if (rawVal === null || rawVal === undefined || rawVal === "") {
                    valueText = null;
                } else if (Array.isArray(rawVal)) {
                    values = rawVal.filter(function (v) {
                        return v !== null && v !== undefined && v !== "";
                    });
                } else {
                    values = [rawVal];
                }
                if (values.length === 0) {
                    valueText = null;
                } else if (values.length === 1) {
                    const v0 = values[0];
                    const oneLabel = optionMap[v0] != null ? optionMap[v0] : optionMap[String(v0)];
                    valueText = oneLabel != null ? oneLabel : String(v0);
                } else {
                    const v0 = values[0];
                    const firstLabel = optionMap[v0] != null ? optionMap[v0] : optionMap[String(v0)];
                    const firstStr = firstLabel != null ? firstLabel : String(v0);
                    valueText = `${firstStr} (+${values.length - 1})`;
                }
            } else if (filter.type === "composite") {
                const filterValue = this.getFilterValue(filter);
                if (filterValue && filterValue.selectValue && filterValue.inputValue) {
                    const optionMap = filter.options.reduce((acc, opt) => {
                        acc[opt.value] = opt.label;
                        return acc;
                    }, {});
                    const selectLabel = optionMap[filterValue.selectValue] || filterValue.selectValue;
                    valueText = `${selectLabel} - ${filterValue.inputValue}`;
                }
            } else {
                const value = this.filterValues[filter.field];
                // Object ise string'e çevir, değilse olduğu gibi kullan
                if (value && typeof value === 'object' && !Array.isArray(value)) {
                    valueText = JSON.stringify(value);
                } else {
                    valueText = value;
                }
            }
            // Her durumda string döndür (null ise empty string)
            return valueText != null ? String(valueText) : "";
        },
        onStateChange(value) {
            // lastConversationStatus filtresi varsa sadece "all" kabul et
            if (this.hasLastConversationStatusFilter && value !== "all") {
                return; // Değişikliği engelle
            }

            this.cloneState = value;
            this.$emit("filters-changed", {
                state: this.cloneState,
                filters: this.cloneFilters,
                sort: this.sortValue,
            });
        },
        onSortChange(value) {
            this.sortValue = value;
            this.$emit("filters-changed", {
                state: this.cloneState,
                filters: this.cloneFilters,
                sort: this.sortValue,
            });
        },
        onOperatorChange(filter, value) {
            const existsFilter = this.cloneFilters.find((f) => f.field === filter.field);
            if (existsFilter) {
                existsFilter.operator = value;
                // Operator değiştiğinde emit et
                this.$emit("filters-changed", {
                    state: this.cloneState,
                    filters: this.cloneFilters,
                    sort: this.sortValue,
                });
            } else {
                this.cloneFilters.push({ field: filter.field, value: "", operator: value });
            }
        },
        onFilterChange(filter, value) {
            const oldLength = this.cloneFilters.length;
            const existsFilter = this.cloneFilters.find((f) => f.field === filter.field);
            
            // multiple: true olan filtrelerin value'sunu normalize et (array olarak sakla)
            const normalizeMultipleValue = (val) => {
                if (filter.type === "select") {
                    if (filter.field.includes("contactProperties.")) {
                        const op =
                            (existsFilter && existsFilter.operator) ||
                            this.getFilterOperator(filter) ||
                            "contains";
                        if (op === "equals") {
                            if (val === null || val === undefined || val === "") {
                                return val;
                            }
                            if (Array.isArray(val)) {
                                return val.length ? val[0] : null;
                            }
                            return val;
                        }
                        if (!val) {
                            return [];
                        }
                        if (Array.isArray(val)) {
                            return val;
                        }
                        return [val];
                    }
                    if (filter.multiple) {
                        if (!val) return [];
                        if (Array.isArray(val)) return val;
                        return [val];
                    }
                }
                return val;
            };
            
            const normalizedValue = normalizeMultipleValue(value);
            
            if (!existsFilter) {
                if (filter.field === "metaReferral") {
                    this.cloneFilters.push({ field: filter.field, value: normalizedValue, operator: "exists" });
                } else if (filter.field === "owner") {
                    this.cloneFilters.push({
                        field: filter.field,
                        value: (normalizedValue || []).map((v) => {
                            return {
                                type: v === "unowned" ? "unowned" : "agent",
                                value: v,
                            };
                        }),
                    });
                } else if (filter.field === "lastConversationUsers") {
                    this.cloneFilters.push({
                        field: filter.field,
                        value: (normalizedValue || []).map((v) => {
                            return {
                                type: v === "none" ? "none" : "agent",
                                value: v,
                            };
                        }),
                    });
                } else if (filter.type === "date") {
                    const relativePresets = ["today", "yesterday", "thisWeek", "lastWeek", "last7Days", "last30Days", "thisMonth", "lastMonth"];
                    const isContactPropertyDate = filter.field.includes("contactProperties.");
                    let val = null;
                    let op = existsFilter ? existsFilter.operator : (filter.operator || "between");
                    
                    if (normalizedValue) {
                        if (typeof normalizedValue === "string" && relativePresets.includes(normalizedValue)) {
                            val = normalizedValue;
                        } else if (Array.isArray(normalizedValue) && normalizedValue.length === 2) {
                            // İki tarih (between için)
                            val = [this.$moment(normalizedValue[0]).toISOString(), this.$moment(normalizedValue[1]).toISOString()];
                        } else if (isContactPropertyDate) {
                            // contactProperties date alanları için timezone kaymasını önlemek adına sadece tarih string'i gönder
                            val = this.$moment(normalizedValue).format("YYYY-MM-DD");
                        } else {
                            // Tek tarih (equals, greater_than vb. için)
                            val = this.$moment(normalizedValue).toISOString();
                        }
                    }
                    this.cloneFilters.push({ field: filter.field, value: val, operator: op });
                } else if (filter.field.includes("contactProperties.")) {
                    let op;
                    if (filter.type === "select") {
                        op = "contains";
                    } else if (filter.type === "text") {
                        op = "contains";
                    } else if (filter.type === "number") {
                        op = "equals";
                    } else {
                        op = undefined;
                    }
                    this.cloneFilters.push({ field: filter.field, value: normalizedValue, operator: op });
                } else {
                    this.cloneFilters.push({ field: filter.field, value: normalizedValue });
                }
            }

            this.cloneFilters = this.cloneFilters
                .map((f) => {
                    if (filter.field === f.field) {
                        const updatedValue = normalizeMultipleValue(value);
                        
                        if (filter.field === "owner") {
                            f.value = (updatedValue || []).map((v) => {
                                return {
                                    type: v === "unowned" ? "unowned" : "agent",
                                    value: v,
                                };
                            });
                        } else if (filter.field === "lastConversationUsers") {
                            f.value = (updatedValue || []).map((v) => {
                                return {
                                    type: v === "none" ? "none" : "agent",
                                    value: v,
                                };
                            });
                        } else {
                            if (filter.type === "date") {
                                const relativePresets = ["today", "yesterday", "thisWeek", "lastWeek", "last7Days", "last30Days", "thisMonth", "lastMonth"];
                                const isContactPropertyDate = filter.field.includes("contactProperties.");
                                if (typeof updatedValue === "string" && relativePresets.includes(updatedValue)) {
                                    f.value = updatedValue;
                                } else if (Array.isArray(updatedValue) && updatedValue.length === 2) {
                                    // İki tarih (between için)
                                    f.value = [this.$moment(updatedValue[0]).toISOString(), this.$moment(updatedValue[1]).toISOString()];
                                } else if (updatedValue && isContactPropertyDate) {
                                    // contactProperties date alanları için timezone kaymasını önlemek adına sadece tarih string'i gönder
                                    f.value = this.$moment(updatedValue).format("YYYY-MM-DD");
                                } else if (updatedValue) {
                                    // Tek tarih (equals, greater_than vb. için)
                                    f.value = this.$moment(updatedValue).toISOString();
                                } else {
                                    f.value = null;
                                }
                            } else {
                                f.value = updatedValue;
                            }
                        }

                        if (filter.operator) {
                            if (filter.type === "date") {
                                f.value.operator = filter.operator;
                            } else if (filter.type === "select") {
                                f.value.operator = filter.operator;
                            } else if (filter.field.includes("contactProperties.")) {
                                f.value = f.value.map((v) => {
                                    return {
                                        ...v,
                                        operator: filter.operator,
                                    };
                                });
                            } else {
                                f.value.operator = filter.operator;
                            }
                        }
                    }
                    return f;
                })
                .filter((f) => {
                    const val = _.get(f, "value.value") || f.value;
                    // Array ise boş olmamalı, diğer değerler için de null/undefined/empty string kontrolü
                    if (Array.isArray(val)) {
                        return val.length > 0;
                    }
                    return val !== null && val !== undefined && val !== "";
                });

            // lastConversationStatus filtresi varsa state'i "all" yap
            const hasLastConversationStatusFilter = this.cloneFilters.some(f => f.field === "lastConversationStatus" && f.value);
            if (hasLastConversationStatusFilter && this.cloneState !== "all") {
                this.cloneState = "all";
            }

            // Sadece gerçekten yeni filtre eklendiğinde popover'ı aç
            if (!existsFilter && this.cloneFilters.length > oldLength) {
                this.$nextTick(() => {
                    setTimeout(() => {
                        const staticFilterChipRef = this.$refs[`staticFilterChip_${filter.field}`];
                        let chipComponent = null;

                        if (staticFilterChipRef) {
                            chipComponent = Array.isArray(staticFilterChipRef) ? staticFilterChipRef[0] : staticFilterChipRef;
                        }

                        // Sadece ref ile openPopover metodunu çağır
                        if (chipComponent && chipComponent.openPopover) {
                            chipComponent.openPopover();
                        }
                    }, 200);
                });
            }
        },
        addDynamicFilter(selectedFilters) {
            const oldFilters = [...this.selectedDynamicFilters];
            this.selectedDynamicFilters = selectedFilters;

            // Yeni eklenen filtreleri bul
            const newlyAddedFilters = selectedFilters.filter(field => !oldFilters.includes(field));

            // "Filtre Ekle" popover'ını kapat
            if (this.$refs.filterAddChip) {
                this.$refs.filterAddChip.closePopover();
            }

            // Sadece yeni filtre eklendiyse popover'ı aç
            if (newlyAddedFilters.length > 0) {
                const lastAddedFilter = newlyAddedFilters[newlyAddedFilters.length - 1];

                this.$nextTick(() => {
                    // DOM'un tamamen render edilmesi için birkaç tick bekle
                    this.$nextTick(() => {
                        setTimeout(() => {
                            // ref ile component'e erişmeyi dene
                            const filterChipRef = this.$refs[`filterChip_${lastAddedFilter}`];
                            let chipComponent = null;

                            if (filterChipRef) {
                                // Vue'da v-for ile ref kullanırken array döner
                                chipComponent = Array.isArray(filterChipRef) ? filterChipRef[0] : filterChipRef;
                            }

                            // Sadece ref ile openPopover metodunu çağır, DOM manipülasyonu yapma
                            if (chipComponent && chipComponent.openPopover) {
                                chipComponent.openPopover();
                            }
                        }, 200);
                    });
                });
            }
        },
        removeDynamicFilter(filter) {
            this.selectedDynamicFilters = this.selectedDynamicFilters.filter((field) => field !== filter.field);
            this.cloneFilters = this.cloneFilters.filter((f) => f.field !== filter.field);
        },
        clearFilter(filter) {
            this.cloneFilters = this.cloneFilters.filter((f) => f.field !== filter.field);
        },
    },
    watch: {
        filters: {
            handler(newVal, oldVal) {
                const isChanged = !_.isEqual(newVal, this.cloneFilters);
                if (isChanged) {
                    const oldLength = this.cloneFilters.length;
                    this.cloneFilters = _.cloneDeep(newVal);
                    const dynamicFilterFields = this.dynamicFilterTemplates.map((filter) => filter.field);
                    const filterFields = newVal.filter((filter) => dynamicFilterFields.includes(filter.field)).map((filter) => filter.field);
                    this.$set(this, "selectedDynamicFilters", filterFields);

                    // Yeni filtre eklendiğinde scroll'u sağa kaydır
                    if (oldVal && newVal.length > oldLength) {
                        this.$nextTick(() => {
                            this.scrollToEnd();
                        });
                    }
                }
            },
            deep: true,
            immediate: true,
        },
        cloneFilters: {
            handler(newVal, oldVal) {
                let isChanged = !_.isEqual(newVal, this.filters);
                if (isChanged) {
                    this.$emit("filters-changed", {
                        state: this.cloneState,
                        filters: newVal,
                        sort: this.sortValue,
                    });

                    // Yeni filtre eklendiğinde scroll'u sağa kaydır
                    if (oldVal && newVal.length > oldVal.length) {
                        this.$nextTick(() => {
                            this.scrollToEnd();
                        });
                    }
                }
            },
            deep: true,
        },
        state: {
            handler(newVal) {
                this.cloneState = newVal;
            },
            deep: true,
            immediate: true,
        },
        cloneState: {
            handler(newVal) {
                this.$emit("filters-changed", {
                    state: newVal,
                    filters: this.cloneFilters,
                    sort: this.sortValue,
                });
                this.cloneState = newVal;
            },
            deep: true,
        },
        sort: {
            handler(newVal) {
                this.sortValue = newVal;
            },
            immediate: true,
        },
    },
};
</script>

<style lang="scss" scoped>
.filters-section {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    flex-shrink: 0;
    margin-top: 10px;
    margin-bottom: 10px;

    .filters-main-container {
        display: flex;
        flex-wrap: wrap;
        gap: 0;
        align-items: flex-start;
        flex: 1;
        min-width: 0;

        /* FilterChip boyutlarını %10 küçült */
        ::v-deep .filter-chip-container,
        ::v-deep .chip {
            transform: scale(0.9);
            transform-origin: left center;
        }

        .contact-count {
            display: flex;
            align-items: center;
            flex-shrink: 0;
            align-self: center;

            .count-text {
                color: #6b7785;
                font-size: 14px;
                font-weight: 500;
                letter-spacing: 0.01em;
                white-space: nowrap;
            }
        }

        .scrollable-filters-wrapper {
            display: flex;
            flex-wrap: wrap;
            gap: 0;
            align-items: center;
            flex: 1;
            min-width: 0;
        }

        .scrollable-filters {
            display: flex;
            flex-wrap: wrap;
            gap: 0;
            align-items: center;
            flex: 1;
            min-width: 0;

            /* Filter chip'lerin küçülmemesi için */
            > * {
                flex-shrink: 0;
                white-space: nowrap;
            }
        }
    }

    .action-buttons-container {
        display: flex;
        gap: 5.2px;
        align-items: flex-start;
        flex-shrink: 0;

        /* FilterChip boyutlarını %10 küçült */
        ::v-deep .filter-chip-container,
        ::v-deep .chip {
            transform: scale(0.9);
            transform-origin: left center;
        }

        ::v-deep .filter-add-chip .chip--add {
            background: #f3f4f6 !important;
            border-style: solid !important;
            border-color: #d1d5db !important;
            color: #22272d !important;

            &:hover {
                background: #e5e7eb !important;
                border-color: #9ca3af !important;
                color: #22272d !important;

                .material-icons {
                    color: #22272d !important;
                }
            }

            .material-icons {
                color: #22272d !important;
            }
        }

        ::v-deep .chip {
            background: #f3f4f6 !important;
            border-style: solid !important;
            border-color: #d1d5db !important;
            color: #22272d !important;

            &:hover {
                background: #e5e7eb !important;
                border-color: #9ca3af !important;
                color: #22272d !important;

                .material-icons {
                    color: #22272d !important;
                }
            }

            .material-icons {
                color: #22272d !important;
            }
        }
    }

    .option-icon-component {
        width: 16px;
        height: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .button-icon-component {
        width: 18px;
        height: 18px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }
}
</style>
